import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  listAdminCases,
  deleteCase,
  toggleCaseStatus,
  reorderCase,
  duplicateCase,
} from "@/lib/admin-cases.functions";

export const Route = createFileRoute("/_authenticated/admin/cases/")({
  head: () => ({
    meta: [
      { title: "Cases — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CasesList,
});

type StatusFilter = "all" | "published" | "draft";

function CasesList() {
  const fn = useServerFn(listAdminCases);
  const delFn = useServerFn(deleteCase);
  const toggleFn = useServerFn(toggleCaseStatus);
  const reorderFn = useServerFn(reorderCase);
  const dupFn = useServerFn(duplicateCase);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "cases"],
    queryFn: () => fn(),
  });

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [busy, setBusy] = useState<string | null>(null);

  const cases = data?.cases ?? [];
  const counts = useMemo(() => ({
    total: cases.length,
    published: cases.filter((c) => c.status === "published").length,
    draft: cases.filter((c) => c.status !== "published").length,
  }), [cases]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter((c) => {
      if (status === "published" && c.status !== "published") return false;
      if (status === "draft" && c.status === "published") return false;
      if (!q) return true;
      return [c.client_name, c.slug, c.format, c.city, c.client_industry]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q));
    });
  }, [cases, search, status]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "cases"] });

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Radera caset för "${name}"? Kan inte ångras.`)) return;
    setBusy(id);
    try {
      await delFn({ data: { id } });
      invalidate();
    } finally { setBusy(null); }
  };

  const handleToggle = async (id: string, current: string) => {
    setBusy(id);
    try {
      await toggleFn({ data: { id, status: current === "published" ? "draft" : "published" } });
      invalidate();
    } finally { setBusy(null); }
  };

  const handleReorder = async (id: string, direction: "up" | "down") => {
    setBusy(id);
    try {
      await reorderFn({ data: { id, direction } });
      invalidate();
    } finally { setBusy(null); }
  };

  const handleDuplicate = async (id: string) => {
    setBusy(id);
    try {
      await dupFn({ data: { id } });
      invalidate();
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Cases</h1>
          <p className="text-sm text-white/50">
            {counts.total} totalt · {counts.published} publicerade · {counts.draft} utkast
          </p>
        </div>
        <Link
          to="/admin/cases/$id"
          params={{ id: "new" }}
          className="rounded-full bg-mint px-4 py-2 text-sm font-medium text-mint-foreground"
        >
          + Nytt case
        </Link>
      </header>

      <div className="flex flex-wrap gap-3 items-center">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sök klient, slug, format, stad…"
          className="flex-1 min-w-[240px] rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-white/30"
        />
        <div className="flex rounded-lg border border-white/10 overflow-hidden text-xs">
          {(["all", "published", "draft"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-2 ${status === s ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}
            >
              {s === "all" ? "Alla" : s === "published" ? "Publicerade" : "Utkast"}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase tracking-wider text-white/50">
            <tr>
              <th className="text-left px-3 py-3 w-16">Ordning</th>
              <th className="text-left px-3 py-3 w-14"></th>
              <th className="text-left px-3 py-3">Klient</th>
              <th className="text-left px-3 py-3">Format</th>
              <th className="text-left px-3 py-3">Stad</th>
              <th className="text-left px-3 py-3">Innehåll</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && <tr><td colSpan={8} className="px-4 py-6 text-white/50">Laddar…</td></tr>}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-6 text-white/50">Inga cases matchar.</td></tr>
            )}
            {filtered.map((c, idx) => (
              <tr key={c.id} className="hover:bg-white/[0.03] align-middle">
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      title="Flytta upp"
                      disabled={busy === c.id || idx === 0 || !!search || status !== "all"}
                      onClick={() => handleReorder(c.id, "up")}
                      className="w-6 h-6 rounded text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30"
                    >↑</button>
                    <button
                      title="Flytta ner"
                      disabled={busy === c.id || idx === filtered.length - 1 || !!search || status !== "all"}
                      onClick={() => handleReorder(c.id, "down")}
                      className="w-6 h-6 rounded text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30"
                    >↓</button>
                    <span className="text-xs text-white/40 ml-1 tabular-nums">{c.sort_index}</span>
                  </div>
                </td>
                <td className="px-3 py-3">
                  {c.hero_image_url ? (
                    <img
                      src={c.hero_image_url}
                      alt={`Thumbnail för ${c.client_name}`}
                      className="w-10 h-10 rounded object-cover border border-white/10"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-white/5 border border-white/10" />
                  )}
                </td>
                <td className="px-3 py-3">
                  <Link to="/admin/cases/$id" params={{ id: c.id }} className="text-white hover:underline">
                    {c.client_name}
                  </Link>
                  <div className="text-xs text-white/40">
                    /{c.slug}
                    {c.client_industry && <span className="ml-2">· {c.client_industry}</span>}
                  </div>
                </td>
                <td className="px-3 py-3 text-white/70">{c.format}</td>
                <td className="px-3 py-3 text-white/60">{c.city ?? "—"}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1 text-[10px] uppercase tracking-wider text-white/50">
                    {c.video_url && <span className="px-1.5 py-0.5 rounded bg-white/5">video</span>}
                    {c.client_quote && <span className="px-1.5 py-0.5 rounded bg-white/5">citat</span>}
                    {c.outcome_metric && <span className="px-1.5 py-0.5 rounded bg-white/5">resultat</span>}
                    {c.body && <span className="px-1.5 py-0.5 rounded bg-white/5">brödtext</span>}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <button
                    onClick={() => handleToggle(c.id, c.status)}
                    disabled={busy === c.id}
                    className={`text-xs rounded-full px-2 py-0.5 transition ${
                      c.status === "published"
                        ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                        : "bg-white/10 text-white/60 hover:bg-white/20"
                    }`}
                    title="Klicka för att växla status"
                  >
                    {c.status === "published" ? "publicerad" : "utkast"}
                  </button>
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {c.status === "published" && (
                    <a
                      href={`/case/${c.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-white/60 hover:text-white mr-3"
                    >
                      Visa ↗
                    </a>
                  )}
                  <button
                    onClick={() => handleDuplicate(c.id)}
                    disabled={busy === c.id}
                    className="text-xs text-white/60 hover:text-white mr-3"
                  >
                    Duplicera
                  </button>
                  <button
                    onClick={() => handleDelete(c.id, c.client_name)}
                    disabled={busy === c.id}
                    className="text-xs text-rose-300 hover:text-rose-200"
                  >
                    Radera
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-white/40">
        Tips: rensa sök &amp; visa "Alla" för att kunna sortera om.
      </p>
    </div>
  );
}
